import { ComputedDataKey, RideComputedDataKeys } from "../../constants";
import { MIN_LIVE_MAP_REFRESH_INTERVAL_SEC } from "../../constants/live-map.const";
import { IRideTableDto } from "../../dto";
import {
  RideDelayStatusEnum,
  sortedRideDelayStatus,
  TimeZoneEnum,
} from "../../enums";
import { DatesUnit } from "../../models";
import {
  dateISOApplyTimezone,
  dateToComparableNumber,
  isValidDateTime,
  nowInTimezone,
} from "../date.helpers";

/** Accepts hex / rgb(a) / hsl(a); rejects empty, transparent, and random strings so Leaflet falls back to ride-grid palette colors. */
export const isCourseLineColorUsable = (value: string): boolean => {
  const v = value.trim().toLowerCase();
  if (!v || v === "transparent" || v === "inherit" || v === "initial") {
    return false;
  }
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) {
    return true;
  }
  if (/^rgba?\([^)]+\)$/i.test(v)) {
    return true;
  }
  if (/^hsla?\([^)]+\)$/i.test(v)) {
    return true;
  }
  return false;
};

/**
 * Polyline / row tint: validated course color when usable (display override), otherwise palette color
 * from shared ride id → color id map (`routeColorIdMatchesResolvedSelector`).

 */
export const getLiveMapRouteLineColor = ({
  rideId,
  courseLineColor,
  getFallbackColorByRideId,
}: {
  rideId: number;
  courseLineColor: string | undefined;
  getFallbackColorByRideId: (rideId: number) => string;
}): string => {
  const trimmed = courseLineColor?.trim() ?? "";
  if (trimmed && isCourseLineColorUsable(trimmed)) {
    return trimmed;
  }
  return getFallbackColorByRideId(rideId);
};

/**
 * Map row tint from ride DTO: prefers `computedData.courseLineColor` when usable, else rides-grid color by `ride.id`.
 */
export const getLiveMapRowLineColor = (
  ride: IRideTableDto,
  getFallbackColorByRideId: (rideId: number) => string,
): string =>
  getLiveMapRouteLineColor({
    rideId: ride.id,
    courseLineColor:
      ride[ComputedDataKey]?.[RideComputedDataKeys.courseLineColor],
    getFallbackColorByRideId,
  });

/**
 * Normalizes copy for `rideMap.ROW_NEW_INCIDENTS_TOOLTIP`: removes the legacy “(New)” / “(חדש)” suffix
 * so the same i18n key can stay stable while the UI no longer shows that qualifier.
 */
export const formatRideMapRowNewIncidentsTooltip = (
  translated: string,
): string =>
  translated
    .replace(/\s*\(\s*New\s*\)\s*$/iu, "")
    .replace(/\s*\(\s*חדש\s*\)\s*$/u, "")
    .trimEnd();

/** Empty / missing live-map delay bucket is shown as NOT_TRACKED (same as legacy grouping). */
export const resolveLiveMapDelayStatusForDisplay = (
  status: RideDelayStatusEnum | null | undefined,
): RideDelayStatusEnum => status ?? RideDelayStatusEnum.NOT_TRACKED;

/** Order for group headers; unknown values sort after `PAST`. */
export const getRideDelayStatusSortRank = (value: unknown): number => {
  const status = resolveLiveMapDelayStatusForDisplay(
    value as RideDelayStatusEnum | null | undefined,
  );
  const idx = sortedRideDelayStatus.indexOf(status);
  return idx === -1 ? sortedRideDelayStatus.length : idx;
};

/** Resolves the active live-map hours value: session override → systemParam → hardcoded fallback. */
export const resolveLiveMapHours = ({
  overrideValue,
  systemParamValue,
  fallback,
}: {
  overrideValue?: number;
  systemParamValue?: number;
  fallback: number;
}): number => {
  if (typeof overrideValue === "number" && overrideValue >= 0) {
    return overrideValue;
  }
  if (typeof systemParamValue === "number" && systemParamValue >= 0) {
    return systemParamValue;
  }
  return fallback;
};

/** Live-map rideStart window: now − hoursBack … now + hoursForward in company timezone, stored as UTC ISO. */
export const computeLiveMapDateRangeFromHours = ({
  timezone,
  hoursBack,
  hoursForward,
  referenceNow,
}: {
  timezone: TimeZoneEnum;
  hoursBack: number;
  hoursForward: number;
  /** Fixed instant for tests; defaults to current time in `timezone`. */
  referenceNow?: TDateISO;
}): DatesUnit => {
  const now = referenceNow
    ? dateISOApplyTimezone(referenceNow, timezone)
    : nowInTimezone(timezone);
  const date1 = now.subtract(hoursBack, "hour").utc().toISOString() as TDateISO;
  const date2 = now.add(hoursForward, "hour").utc().toISOString() as TDateISO;

  const dateRange: DatesUnit = { date1, date2 };
  return dateRange;
};

/** True when live-map window end falls on a later local calendar day than its start. */
export const doesLiveMapDateRangeCrossLocalDay = ({
  dateRange,
  timezone,
}: {
  dateRange: DatesUnit;
  timezone: TimeZoneEnum;
}): boolean => {
  const startDay = dateISOApplyTimezone(dateRange.date1, timezone).format(
    "YYYY-MM-DD",
  );
  const endDay = dateISOApplyTimezone(dateRange.date2, timezone).format(
    "YYYY-MM-DD",
  );

  return endDay > startDay;
};

/** Returns true when a ride's delay-status group should appear in the live-map grid. */
export const isLiveMapRideDelayStatusVisible = (
  status: RideDelayStatusEnum | undefined,
  hiddenStatuses: readonly RideDelayStatusEnum[],
): boolean => {
  const resolvedStatus = resolveLiveMapDelayStatusForDisplay(status);
  return !hiddenStatuses.includes(resolvedStatus);
};

/** Filters live-map grid rows by toolbar delay-status chip visibility. */
export const filterLiveMapRowsByHiddenDelayStatus = (
  rides: readonly IRideTableDto[] | undefined,
  hiddenStatuses: readonly RideDelayStatusEnum[],
): IRideTableDto[] => {
  if (!rides?.length || !hiddenStatuses.length) {
    return rides ? [...rides] : [];
  }

  return rides.filter(ride =>
    isLiveMapRideDelayStatusVisible(ride.statusLate, hiddenStatuses),
  );
};

/** Ride is inside its planned window: start passed, end not reached. */
export const isRidePlannedForNow = ({
  ride,
  now,
}: {
  ride: Pick<IRideTableDto, "rideStart" | "rideEnd">;
  now: TDateISO | Date;
}): boolean => {
  const { rideStart, rideEnd } = ride;

  if (!isValidDateTime(rideStart) || !isValidDateTime(rideEnd)) {
    return false;
  }

  const nowMs = dateToComparableNumber(
    now instanceof Date ? (now.toISOString() as TDateISO) : now,
  );

  return (
    dateToComparableNumber(rideStart) <= nowMs &&
    dateToComparableNumber(rideEnd) > nowMs
  );
};

/** Counts loaded live-map rows currently inside their planned time window. */
export const countLiveMapRidesPlannedForNow = ({
  rides,
  now,
}: {
  rides: readonly IRideTableDto[];
  now: TDateISO | Date;
}): number =>
  rides.reduce(
    (count, ride) => count + (isRidePlannedForNow({ ride, now }) ? 1 : 0),
    0,
  );

/** Counts live-map grid rows per resolved delay-status bucket. */
export const countLiveMapRidesByDelayStatus = (
  rides: readonly IRideTableDto[],
): Partial<Record<RideDelayStatusEnum, number>> => {
  const counts: Partial<Record<RideDelayStatusEnum, number>> = {};

  for (const ride of rides) {
    const status = resolveLiveMapDelayStatusForDisplay(ride.statusLate);
    counts[status] = (counts[status] ?? 0) + 1;
  }

  return counts;
};

const normalizeLiveMapRefreshIntervalSec = (seconds: number): number =>
  Math.max(MIN_LIVE_MAP_REFRESH_INTERVAL_SEC, Math.floor(seconds));

/** Resolves live-map polling interval (seconds): session override → systemParam → hardcoded fallback. */
export const resolveLiveMapRefreshIntervalSec = ({
  overrideValue,
  systemParamValue,
  fallback,
}: {
  overrideValue?: number;
  systemParamValue?: number;
  fallback: number;
}): number => {
  if (typeof overrideValue === "number" && overrideValue >= 1) {
    return normalizeLiveMapRefreshIntervalSec(overrideValue);
  }
  if (typeof systemParamValue === "number" && systemParamValue >= 1) {
    return normalizeLiveMapRefreshIntervalSec(systemParamValue);
  }
  return normalizeLiveMapRefreshIntervalSec(fallback);
};
